import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addTrackingDetails,
  saveOnOrderModel,
  getAllOrdersForAccountsAPI,
  getCustomerOrder,
  getOrdersRoleBased,
  updateOrderStatusByAccount,
  updatePaymentDetails,
  validatePaymentByAccount,
  cancelledOrder,
  assignWarehouseToOrder,
  getFullyDispatchedOrders,
  getPendingDispatchOrders,
  getPendingWarehouseAssignment,
  creditPaymentApi,
  getCreditRequestHistoryApi,
  getPendingCreditRequestsApi,
  approveCreditRequestApi,
  updateOrderAddressApi,
  getPartiallyDispatchedOrders,
  checkStockAvailabilityApi,
  getEpcOrdersApi,
  removeAssignedWarehouseApi,
  getDispatchedOrders,
  moveOrderToCompletedApi,
} from "../../api/order.api";
import {
  CustomerOrder,
  IOrder,
  IOrderValidation,
  ItrackingDetails,
} from "../../models/Order.model";
import toast from "react-hot-toast";
import ResponseModel from "../../models/response.model";

interface OrderSectionState {
  data: IOrder[];
  pagination: any;
}

interface OrderState {
  orders: IOrder[];
  orderPayments: IOrder[];
  pendingOrders: OrderSectionState;
  approvedOrders: OrderSectionState;
  cancelledOrders: OrderSectionState;
  rejectedOrders: OrderSectionState;
  creditedOrders: OrderSectionState;
  ordersTotalPages: number;
  pendingCreditRequests: OrderSectionState;
  creditHistory: OrderSectionState;
  isCheckingStock: boolean;
  loading: boolean;
  error: string | null;
  customerOrders: CustomerOrder[];
  pendingAssignment: IOrder[];
  pendingDispatch: IOrder[];
  partiallyDispatched: IOrder[];
  fullyDispatched: IOrder[];
  epcOrders: IOrder[];
  paginations: {
    pendingAssignment?: any;
    pendingDispatch?: any;
    partiallyDispatched?: any;
    fullyDispatched?: any;
    epcOrders?: any;
  };
  stockAvailability: {
    isFullyAvailable: boolean;
    items: Array<{
      itemId: string;
      name: string;
      requiredQty: number;
      availableQty: number;
      hasEnoughStock: boolean;
      unit: string;
    }>;
  } | null;
}

const initialSection: OrderSectionState = {
  data: [],
  pagination: { page: 1, limit: 10, totalCount: 0, totalPages: 0 },
};

const initialState: OrderState = {
  orders: [],
  orderPayments: [],
  ordersTotalPages: 0,
  pendingOrders: initialSection,
  approvedOrders: initialSection,
  cancelledOrders: initialSection,
  rejectedOrders: initialSection,
  creditedOrders: initialSection,
  isCheckingStock: false,
  loading: false,
  error: null,
  customerOrders: [],
  pendingAssignment: [],
  pendingDispatch: [],
  partiallyDispatched: [],
  fullyDispatched: [],
  epcOrders: [],
  paginations: {},
  pendingCreditRequests: initialSection,
  creditHistory: initialSection,
  stockAvailability: null,
};

export const createOrder = createAsyncThunk(
  "orders/createOrder",
  async (
    orderData: {
      leadId: string;
      goods: { productId: string; quantity: number }[];
      gstin: string;
      customerId: string;
      orderNumber: string;
      quotationId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await saveOnOrderModel(orderData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to create order.");
    }
  },
);

export const removeAssignedWarehouse = createAsyncThunk(
  "orders/removeAssignedWarehouse",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await removeAssignedWarehouseApi(orderId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to create order.");
    }
  },
);

export const moveOrderToCompleted = createAsyncThunk<
  ResponseModel, // fulfilled payload
  string, // argument
  { rejectValue: ResponseModel } // rejected payload
>("orders/moveOrderToCompleted", async (orderId, { rejectWithValue }) => {
  try {
    const response = await moveOrderToCompletedApi(orderId);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data ?? {
        error: "Failed to move order to completed",
      },
    );
  }
});

export const fetchCustomerOrder = createAsyncThunk(
  "orders/fetchCustomerOrder",
  async ({ leadId }: { leadId: string }, { rejectWithValue }) => {
    try {
      const response = await getCustomerOrder(leadId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch order");
    }
  },
);

export const fetchAllOrdersForAccounts = createAsyncThunk(
  "orders/fetchOrders",
  async ({
    page = 1,
    limit = 12,
    search,
    status,
    payments,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    payments: boolean;
    status?: string;
  }) => {
    const response = await getAllOrdersForAccountsAPI({
      page,
      limit,
      search,
      status,
      payments,
    });
    return response;
  },
);

export const fetchOrdersRoleBased = createAsyncThunk(
  "orders/fetchOrdersRoleBased",
  async (
    params: {
      status: string;
      search?: string;
      page?: number;
      createdBy?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await getOrdersRoleBased(
        params.status,
        params.search,
        params.page,
        params.createdBy,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch orders");
    }
  },
);

export const addTrackingDetail = createAsyncThunk(
  "orders/addTrackingDetails",
  async (
    {
      orderId,
      goods,
      trackingDetails,
    }: {
      orderId: string;
      goods: { productId: string; quantity: number }[];
      trackingDetails: ItrackingDetails[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await addTrackingDetails(
        orderId,
        goods,
        trackingDetails,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to add tracking details",
      );
    }
  },
);

export const addPaymentDetails = createAsyncThunk(
  "order/addPaymentDetails",
  async (formData: FormData) => {
    const response = await updatePaymentDetails(formData);
    return response.data.data;
  },
);

export const verifyPaymentByAccount = createAsyncThunk(
  "orders/updatePaymentPrice",
  async (
    {
      orderId,
      utrNumbers,
      screenshots,
    }: {
      orderId: string;
      utrNumbers?: string[];
      screenshots?: string[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await validatePaymentByAccount(
        orderId,
        utrNumbers,
        screenshots,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update payment details",
      );
    }
  },
);

export const updateOrderStatus = createAsyncThunk(
  "orders/updateOrderStatus",
  async (
    { id, status }: { id: string; status: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateOrderStatusByAccount(id, status);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update order status",
      );
    }
  },
);

export const cancelOrder = createAsyncThunk(
  "orders/cancelOrder",
  async (orderId: string, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await cancelledOrder(orderId);
      const state: any = getState();
      dispatch(fetchOrdersRoleBased({ status: state.selectedStatus }));
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to cancel order");
    }
  },
);

export const fetchPendingAssignment = createAsyncThunk(
  "orders/fetchPendingAssignment",
  async (
    {
      page = 1,
      search = "",
      startDate = "",
      endDate = "",
    }: {
      page?: number;
      search?: string;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await getPendingWarehouseAssignment({
        page,
        search,
        startDate,
        endDate,
      });
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to fetch pending assignments";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  },
);

export const fetchPendingDispatch = createAsyncThunk(
  "orders/fetchPendingDispatch",
  async (
    {
      page = 1,
      search = "",
      startDate = "",
      endDate = "",
    }: {
      page?: number;
      search?: string;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await getPendingDispatchOrders({
        page,
        search,
        startDate,
        endDate,
      });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending dispatch",
      );
    }
  },
);

export const fetchPartiallyDispatched = createAsyncThunk(
  "orders/fetchPartiallyDispatched",
  async (
    {
      page = 1,
      search = "",
      startDate = "",
      endDate = "",
    }: {
      page?: number;
      search?: string;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await getPartiallyDispatchedOrders({
        page,
        search,
        startDate,
        endDate,
      });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch partial dispatch",
      );
    }
  },
);

export const fetchFullyDispatched = createAsyncThunk(
  "orders/fetchFullyDispatched",
  async (
    {
      page = 1,
      search = "",
      startDate = "",
      endDate = "",
    }: {
      page?: number;
      search?: string;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await getFullyDispatchedOrders({
        page,
        search,
        startDate,
        endDate,
      });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch history",
      );
    }
  },
);

export const fetchDispatched = createAsyncThunk(
  "orders/fetchDispatched",
  async (
    {
      page = 1,
      search = "",
      startDate = "",
      endDate = "",
    }: {
      page?: number;
      search?: string;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await getDispatchedOrders({
        page,
        search,
        startDate,
        endDate,
      });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch history",
      );
    }
  },
);

export const assignWarehouse = createAsyncThunk(
  "orders/assignWarehouse",
  async (
    {
      orderId,
      warehouseId,
      adjustments,
      transportPrice,
      goods,
    }: {
      orderId: string;
      warehouseId: string;
      adjustments: any[];
      transportPrice: string;
      goods: any[];
    },
    { rejectWithValue },
  ) => {
    try {
      return await assignWarehouseToOrder(
        orderId,
        warehouseId,
        adjustments,
        transportPrice,
        goods,
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Assignment failed",
      );
    }
  },
);

export const creditPayment = createAsyncThunk(
  "order/creditPayment",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await creditPaymentApi(orderId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchPendingCreditRequests = createAsyncThunk(
  "orders/fetchPendingCreditRequests",
  async (
    params: { page?: number; limit?: number; search?: string } = {},
    { rejectWithValue },
  ) => {
    try {
      const response = await getPendingCreditRequestsApi(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch requests",
      );
    }
  },
);

export const fetchCreditHistory = createAsyncThunk(
  "orders/fetchCreditHistory",
  async (
    params: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    } = {},
    { rejectWithValue },
  ) => {
    try {
      const response = await getCreditRequestHistoryApi(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch credit history",
      );
    }
  },
);

export const approveCreditRequest = createAsyncThunk(
  "orders/approveCreditRequest",
  async (
    data: {
      orderId: string;
      status: "approved" | "rejected" | "cancelled";
      remark: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await approveCreditRequestApi(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to process credit request",
      );
    }
  },
);

export const updateOrderAddress = createAsyncThunk(
  "order/updateOrderAddress",
  async (
    { orderId, shippingAddress }: { orderId: string; shippingAddress: any },
    { rejectWithValue },
  ) => {
    try {
      const data = await updateOrderAddressApi(orderId, shippingAddress);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const checkWarehouseAvailability = createAsyncThunk(
  "orders/checkWarehouseAvailability",
  async (
    {
      orderId,
      warehouseId,
      goods,
    }: { orderId: string; warehouseId: string; goods: any[] },
    { rejectWithValue },
  ) => {
    try {
      return await checkStockAvailabilityApi(orderId, warehouseId, goods);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Stock check failed",
      );
    }
  },
);

export const fetchEpcOrders = createAsyncThunk(
  "orders/fetchEpcOrders",
  async (
    {
      page = 1,
      search = "",
      startDate = "",
      endDate = "",
    }: { page?: number; search?: string; startDate?: string; endDate?: string },
    { rejectWithValue },
  ) => {
    try {
      return await getEpcOrdersApi({ page, search, startDate, endDate });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch EPC orders",
      );
    }
  },
);

const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    updatePaymentPriceInstant: (state, action) => {
      const { orderId, payment } = action.payload;

      state.orders = state.orders.map((order) =>
        order._id === orderId
          ? {
              ...order,
              paymentDetails: {
                ...order.paymentDetails,
                ...payment,
              },
            }
          : order,
      );
    },
    clearStockAvailability: (state) => {
      state.stockAvailability = null;
    },
    setOrders: (state, action) => {
      state.orders = action.payload;
      state.orderPayments = action.payload;
      state.loading = false;
    },
    dispatchOrder: (state, action) => {
      state.orders = state.orders.map((order) =>
        order._id === action.payload ? { ...order } : order,
      );
    },
    updateOrderTracking: (state, action) => {
      const { orderId, trackingDetails } = action.payload;
      state.orders = state.orders.map((order) =>
        order._id === orderId ? { ...order, trackingDetails } : order,
      );
    },
    updatePaymentDetailsOfOrders: (state, action) => {
      const { orderId, paymentDetails } = action.payload;
      state.orders = state.orders.map((order) =>
        order._id === orderId
          ? {
              ...order,
              paymentDetails: { ...order.paymentDetails, ...paymentDetails },
            }
          : order,
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllOrdersForAccounts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllOrdersForAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.orderPayments = action.payload.data;
        state.ordersTotalPages = action.payload?.pagination?.totalPages || 0;
        state.orders = action.payload.data;
      })
      .addCase(fetchAllOrdersForAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch orders";
      })
      .addCase(addTrackingDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(addTrackingDetail.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addTrackingDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateOrderStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload;
        state.orders = state.orders.map((order) =>
          order._id === updatedOrder._id
            ? { ...order, orderStatus: updatedOrder.orderStatus }
            : order,
        );
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchOrdersRoleBased.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrdersRoleBased.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        const data = payload.data.orders || [];
        const status = payload.data.status || "pending";

        if (status === "pending") {
          state.pendingOrders = {
            data,
            pagination: payload.pagination!,
          };
        }
        if (status === "approved") {
          state.approvedOrders = {
            data,
            pagination: payload.pagination!,
          };
        }
        if (status === "cancelled") {
          state.cancelledOrders = {
            data,
            pagination: payload.pagination!,
          };
        }
        if (status === "rejected") {
          state.rejectedOrders = {
            data,
            pagination: payload.pagination!,
          };
        }
        if (status === "credited") {
          state.creditedOrders = {
            data,
            pagination: payload.pagination!,
          };
        }
      })
      .addCase(fetchOrdersRoleBased.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch orders";
      })
      .addCase(addPaymentDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(addPaymentDetails.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload;
        if (!updatedOrder || !updatedOrder._id) return;
        const index = state.orders.findIndex(
          (order) => order._id === updatedOrder._id,
        );
        if (index !== -1) {
          state.orders[index] = {
            ...state.orders[index],
            paymentDetails: {
              ...state.orders[index].paymentDetails,
              ...updatedOrder.paymentDetails,
            },
          };
        }
      })
      .addCase(addPaymentDetails.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          "Something went wrong while saving payment details";
      })
      .addCase(fetchCustomerOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCustomerOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.customerOrders = action.payload;
      })
      .addCase(fetchCustomerOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch customer orders";
      })
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(moveOrderToCompleted.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(moveOrderToCompleted.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(moveOrderToCompleted.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.error || "Failed to move order to completed";
      })
      .addCase(removeAssignedWarehouse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeAssignedWarehouse.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeAssignedWarehouse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyPaymentByAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyPaymentByAccount.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload;
        state.orders = state.orders.map((order) =>
          order._id === updatedOrder._id
            ? {
                ...order,
                paymentDetails: {
                  ...order.paymentDetails,
                  ...updatedOrder.paymentDetails,
                },
              }
            : order,
        );
      })
      .addCase(verifyPaymentByAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload;
        state.orders = state.orders.map((order) =>
          order._id === updatedOrder._id
            ? {
                ...order,
                orderStatus: IOrderValidation.cancelled,
                cancelledOrder: true,
              }
            : order,
        );
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPendingAssignment.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingAssignment = action.payload.orders;
        state.paginations.pendingAssignment = action.payload.pagination;
      })
      .addCase(fetchPendingAssignment.rejected, (state) => {
        state.loading = false;
      })

      .addCase(fetchPendingDispatch.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingDispatch.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingDispatch = action.payload.orders;
        state.paginations.pendingDispatch = action.payload.pagination;
      })
      .addCase(fetchPendingDispatch.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchPartiallyDispatched.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPartiallyDispatched.fulfilled, (state, action) => {
        state.loading = false;
        state.partiallyDispatched = action.payload.orders;
        state.paginations.partiallyDispatched = action.payload.pagination;
      })
      .addCase(fetchPartiallyDispatched.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchDispatched.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDispatched.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.fullyDispatched = payload.data.orders;
        state.paginations.fullyDispatched = payload.pagination;
      })
      .addCase(fetchDispatched.rejected, (state) => {
        state.loading = false;
      })

      .addCase(fetchFullyDispatched.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFullyDispatched.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.fullyDispatched = payload.data.orders;
        state.paginations.fullyDispatched = payload.pagination;
      })
      .addCase(fetchFullyDispatched.rejected, (state) => {
        state.loading = false;
      })
      .addCase(assignWarehouse.rejected, (state) => {
        state.loading = false;
      })
      .addCase(assignWarehouse.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignWarehouse.fulfilled, (state, action) => {
        state.loading = false;
        const updatedOrder = action.payload;

        state.pendingAssignment = state.pendingAssignment.filter(
          (o) => o._id !== updatedOrder._id,
        );

        if (state.paginations.pendingAssignment) {
          state.paginations.pendingAssignment.totalCount -= 1;
        }

        state.partiallyDispatched = state.partiallyDispatched.map((o) =>
          o._id === updatedOrder._id && o.dispatch === "partiallyDispatched"
            ? { ...o, dispatch: "partial" }
            : o,
        );
      })
      .addCase(creditPayment.pending, (state) => {
        state.loading = true;
      })
      .addCase(creditPayment.fulfilled, (state, action) => {
        state.loading = false;
        const orderId = action.meta.arg;
        state.pendingOrders.data = state.pendingOrders.data.filter(
          (order) => order._id !== orderId,
        );
      })
      .addCase(creditPayment.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchPendingCreditRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingCreditRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingCreditRequests = {
          data: action.payload.data.requests || [],
          pagination: action.payload.data.pagination,
        };
      })
      .addCase(fetchPendingCreditRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCreditHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreditHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.creditHistory = {
          data: action.payload.data.requests || [],
          pagination: action.payload.data.pagination,
        };
      })
      .addCase(fetchCreditHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(approveCreditRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveCreditRequest.fulfilled, (state, action) => {
        state.loading = false;
        const processedOrderId = action.meta.arg.orderId;
        state.pendingCreditRequests.data =
          state.pendingCreditRequests.data.filter(
            (order) => order._id !== processedOrderId,
          );
        if (state.pendingCreditRequests.pagination) {
          state.pendingCreditRequests.pagination.totalCount -= 1;
        }
      })
      .addCase(approveCreditRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateOrderAddress.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateOrderAddress.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateOrderAddress.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkWarehouseAvailability.pending, (state) => {
        state.isCheckingStock = true;
        state.stockAvailability = null;
      })
      .addCase(checkWarehouseAvailability.fulfilled, (state, action) => {
        state.isCheckingStock = false;
        state.stockAvailability = action.payload;
      })
      .addCase(checkWarehouseAvailability.rejected, (state) => {
        state.isCheckingStock = false;
        state.stockAvailability = null;
      })
      .addCase(fetchEpcOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEpcOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.epcOrders = action.payload.orders;
        state.paginations.epcOrders = action.payload.pagination;
      })
      .addCase(fetchEpcOrders.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setOrders,
  updatePaymentPriceInstant,
  dispatchOrder,
  updateOrderTracking,
  updatePaymentDetailsOfOrders,
  clearStockAvailability,
} = orderSlice.actions;
export default orderSlice.reducer;
